import React, { useState } from "react";
import { Link } from "react-router-dom";
import { formatUsd } from "@veyso/core";
import type { LanguagesView } from "../api.js";
import { statusLabel } from "../languages/languages.js";
import { exportFilename, rowKey, type ExportRow, type RowRun } from "./languageExports.js";
import { rowChip, type ChipTone } from "./exportView.js";

const CHIP: Record<ChipTone, string> = {
  ok: "bg-[#E7F6EC] text-[#1C6B34]",
  muted: "bg-bay-850 text-ink-300",
  warn: "bg-[#FFF1E0] text-[#8A4300]",
  bad: "bg-[#FDECEC] text-[#B42318]",
  busy: "bg-[#FFF1E0] text-[#8A4300]",
};

/**
 * The Download tab's videos, one row per language: the English first, then
 * every ready language, each its own MP4 rendered in this tab. A row says its
 * state in a chip and keeps its files behind one button, so the list reads at
 * a glance; which rows are checked, and how far an export has got, belong to
 * the page.
 */
export const ExportRows: React.FC<{
  projectId: string;
  projectName: string;
  rows: ExportRow[];
  view: LanguagesView | null;
  checked: readonly string[];
  onToggle: (key: string) => void;
  /** An export is running: nothing may be checked or unchecked. */
  busy: boolean;
  /** Whether this browser can render here; null while it is being asked. */
  canRender: boolean | null;
  runs: Readonly<Record<string, RowRun>>;
  /** The English's latest export, which its row downloads. */
  english: { href: string; filename: string | null } | null;
}> = ({ projectId, projectName, rows, view, checked, onToggle, busy, canRender, runs, english }) => {
  const [open, setOpen] = useState<string | null>(null);

  const byCode = new Map((view?.languages ?? []).map((language) => [language.code, language]));
  // Still being made, a script to check, or failed: shown so they are not missing, with no checkbox.
  const coming = (view?.languages ?? []).filter((one) => one.status !== "ready");
  const editorPanel = (code?: string) =>
    `/projects/${projectId}/editor?languages=open${code ? `&lang=${encodeURIComponent(code)}` : ""}`;

  const filesOf = (row: ExportRow) => {
    const video = row.code === null ? english?.href : byCode.get(row.code)?.downloads.video;
    const name = row.code === null ? (english?.filename ?? true) : exportFilename(projectName, row.code);
    return [
      ...(video ? [{ label: "MP4", href: video, download: name as string | true }] : []),
      ...row.extras.map((extra) => ({ label: extra.label, href: extra.href, download: true as const })),
    ];
  };

  return (
    <div className="overflow-hidden rounded-[12px] border border-[var(--edge)]">
      {rows.map((row) => {
        const key = rowKey(row);
        const language = row.code ? byCode.get(row.code) : undefined;
        const chip = rowChip(row, runs[key], canRender);
        const offered = row.code === null || canRender !== false;
        const files = filesOf(row);
        const expanded = open === key;
        const id = `export-row-${key || "en"}`;
        return (
          <div key={key} className="border-b border-[var(--edge)] last:border-b-0">
            <div className="flex items-center gap-3 px-3.5 py-3">
              {offered ? (
                <input
                  id={id}
                  type="checkbox"
                  className="h-[18px] w-[18px] shrink-0"
                  checked={checked.includes(key)}
                  disabled={busy}
                  onChange={() => onToggle(key)}
                />
              ) : (
                <span className="w-[18px] shrink-0" />
              )}
              <label
                htmlFor={offered ? id : undefined}
                className={`flex min-w-0 flex-1 flex-col gap-0.5 ${offered ? "cursor-pointer" : ""}`}
              >
                <span className="truncate text-[15px] font-semibold text-ink-100" lang={row.code ?? "en"}>
                  {row.label}
                </span>
                <span className="text-[12px] text-ink-300">{row.depthLabel}</span>
              </label>
              <span
                className={`relative shrink-0 overflow-hidden whitespace-nowrap rounded-full px-2 py-1 text-[12px] font-semibold tabular-nums ${CHIP[chip.tone]}`}
                aria-live={chip.tone === "busy" ? "polite" : undefined}
              >
                {chip.progress !== undefined ? (
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 left-0 bg-ember-500/15"
                    style={{ width: `${Math.round(chip.progress * 100)}%` }}
                  />
                ) : null}
                <span className="relative">{chip.label}</span>
              </span>
              {files.length > 0 ? (
                <button
                  type="button"
                  className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border border-[var(--edge)] bg-white hover:border-ember-500 focus-visible:outline-2 focus-visible:outline-[var(--edge-focus)]"
                  aria-expanded={expanded}
                  aria-controls={`${id}-files`}
                  aria-label={`Downloads for ${row.label}`}
                  onClick={() => setOpen(expanded ? null : key)}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                    <path
                      d="M8 2.5v7.5M4.8 7l3.2 3.2L11.2 7M3 13h10"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : (
                <span className="w-[34px] shrink-0" />
              )}
            </div>
            {chip.detail ? <p className="px-3.5 pb-3 pl-[44px] text-[12px] text-ink-300">{chip.detail}</p> : null}
            {row.outOfDate ? (
              // Still exported as it is; making it again from the English as it now is costs.
              <p className="flex flex-wrap items-baseline gap-x-2 px-3.5 pb-3 pl-[44px] text-[12px]">
                <span className="text-ink-300">The English changed since this was made.</span>
                <Link to={editorPanel(row.code ?? undefined)} className="font-semibold text-ember-600 hover:underline">
                  {language?.canRemake ? `Update · ${formatUsd(language.priceUsd)}` : "Open Languages"}
                </Link>
              </p>
            ) : null}
            {expanded ? (
              <div id={`${id}-files`} className="flex flex-wrap gap-1.5 px-3.5 pb-3 pl-[44px]">
                {files.map((file) => (
                  <a
                    key={file.label}
                    href={file.href}
                    download={file.download}
                    className="rounded-full border border-[var(--edge)] px-2.5 py-1 text-[12.5px] text-ink-100 hover:border-ember-500 hover:text-ember-600"
                  >
                    {file.label}
                  </a>
                ))}
              </div>
            ) : null}
          </div>
        );
      })}

      {coming.map((language) => (
        <div
          key={language.code}
          className="flex items-center gap-3 border-b border-[var(--edge)] px-3.5 py-3 last:border-b-0"
        >
          <span className="w-[18px] shrink-0" />
          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="truncate text-[15px] font-semibold text-ink-300" lang={language.code}>
              {language.nativeName}
            </span>
            <span className="text-[12px] text-ink-300">
              {language.depth === "video" ? "Whole video" : "Voice and subtitles"}
            </span>
          </span>
          {language.status === "failed" ? (
            <Link to={editorPanel()} className="text-[12.5px] text-signal-bad hover:underline">
              Couldn't be made — try again
            </Link>
          ) : (
            <Link to={editorPanel(language.code)} className="text-[12.5px] text-ember-600 hover:underline">
              {statusLabel(language).text}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
};
